import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { UserProfile, UserRole } from "./types";

type ProfileWithRoles = {
  id: string;
  username: string | null;
  avatar_url: string | null;
  created_at: string;
  user_roles: { role: UserRole }[] | null;
}

export const useUsers = (sortOrder: "asc" | "desc") => {
  const queryClient = useQueryClient();

  const { data: users = [], isLoading, isError } = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      try {
        console.log('Fetching users...');
        
        // First, get all profiles
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select(`
            id,
            username,
            avatar_url,
            created_at
          `)
          .order('created_at', { ascending: sortOrder === "asc" });

        if (profilesError) {
          console.error('Error fetching profiles:', profilesError);
          throw profilesError;
        }

        // Then, get user roles separately
        const { data: userRoles, error: rolesError } = await supabase
          .from('user_roles')
          .select('user_id, role');

        if (rolesError) {
          console.error('Error fetching user roles:', rolesError);
          throw rolesError;
        }

        // Map roles to profiles
        const usersWithRoles = (profiles || []).map(profile => {
          const userRole = userRoles?.find(role => role.user_id === profile.id);
          return {
            id: profile.id,
            username: profile.username || '',
            avatar_url: profile.avatar_url || '',
            created_at: profile.created_at,
            user_roles: {
              role: (userRole?.role as UserRole) || 'user'
            }
          };
        });

        console.log('Processed users:', usersWithRoles);
        return usersWithRoles;

      } catch (error) {
        console.error('Error in query function:', error);
        toast.error('حدث خطأ أثناء تحميل المستخدمين');
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const updateRole = useMutation({
    mutationFn: async ({ userId, role }: { userId: string, role: UserRole }) => {
      // Delete existing role
      const { error: deleteError } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId);

      if (deleteError) throw deleteError;

      // Insert new role
      const { error: insertError } = await supabase
        .from('user_roles')
        .insert({ user_id: userId, role });

      if (insertError) throw insertError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success('تم تحديث دور المستخدم بنجاح');
    },
    onError: (error) => {
      console.error('Error updating user role:', error);
      toast.error('حدث خطأ أثناء تحديث دور المستخدم');
    },
  });

  return {
    users,
    isLoading,
    isError,
    updateRole
  };
};